// frontend_app/src/lib/html.js
//
// Scraped HTML is sanitised by the backend as well, but it is sanitised again
// here before it touches the DOM (defence in depth, and cached/old payloads).

import DOMPurify from 'dompurify';
import { splitForHighlight } from './highlight';

const ALLOWED_TAGS = [
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup', 'dd', 'div',
  'dl', 'dt', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
  'pre', 's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th',
  'thead', 'tr', 'u', 'ul', 'mark',
];
const ALLOWED_ATTR = ['href', 'title', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan', 'scope', 'start'];

let hooksInstalled = false;
const installHooks = () => {
  if (hooksInstalled) return;
  hooksInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (/^(https?:|mailto:|tel:)/i.test(href)) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      } else {
        node.removeAttribute('href');
      }
    }
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') || '';
      if (!/^https?:/i.test(src)) node.removeAttribute('src');
      node.setAttribute('loading', 'lazy');
      node.setAttribute('referrerpolicy', 'no-referrer');
    }
  });
};

const purifyToFragment = (html) => {
  installHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });
};

/** Wrap highlighter matches found in text nodes of `root` with <mark>/<span>. */
const highlightTextNodes = (root, highlighter) => {
  const doc = root.ownerDocument || document;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    const segments = splitForHighlight(node.nodeValue, highlighter);
    if (!segments.some((s) => s.match)) return;
    const frag = doc.createDocumentFragment();
    segments.forEach(({ text, match }) => {
      if (!match) {
        frag.appendChild(doc.createTextNode(text));
        return;
      }
      const el = doc.createElement(match.type === 'term' ? 'mark' : 'span');
      el.className = match.type === 'term' ? 'hl' : `sample-chip ${match.className}`;
      el.textContent = text;
      frag.appendChild(el);
    });
    node.parentNode.replaceChild(frag, node);
  });
};

/** Sanitised HTML string, with search terms highlighted if a highlighter is given. */
export const sanitizeHtml = (html, highlighter = null) => {
  const fragment = purifyToFragment(html);
  if (highlighter) highlightTextNodes(fragment, highlighter);
  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
};
